"use client"
import React, { useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import useApi from "@/components/api/useApi";
import UralityAvatarImage from "@/components/shared/UralityAvatarImage";

import "@/components/shared/LocationSelector.css";
import { emitEvent } from "@/lib/events";
import { Collapse } from "react-bootstrap";
import LocationAddressEntry from "@/components/shared/LocationAddressEntry";
import MapLink from "@/components/shared/MapLink";
import { useCurrentLocation } from "@/lib/providers/GeolocationProvider";

var moment = require("moment");

var lastTimestamp = null;

const LocationSelector = ({
  name,
  location,
  isNullable,
  isInvalid,
  styles,
  placeholder,
  onBlur,
  onSelect,
  clearOnSelect = false,
  returnNull,
  innerRef,
  className,
  profileSearch,
  addFromMap = false,
  moveEnabled = false,
  onMovePoint,
  fullAddressMode = false,
  searchSource = "MAPBOX",
  searchRadius = 20,
  searchLocation = null,
  useMyLocation = false,
  children
}) => {
  const [value, setValue] = useState(null);
  const [defaultOptions, setDefaultOptions] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [gpsRequested, setGpsRequested] = useState(false);

  const { currentLocation, locationStatus, requestLocation } = useCurrentLocation();

  const targetHeight = 38;

  const defaultStyles = {
    control: base => ({
      ...base,
      minHeight: "initial"
    }),
    valueContainer: base => ({
      ...base,
      height: `${targetHeight - 1 - 1}px`,
      padding: "0 8px"
    }),
    clearIndicator: base => ({
      ...base,
      padding: `${(targetHeight - 20 - 1 - 1) / 2}px`
    }),
    dropdownIndicator: base => ({
      ...base,
      padding: `${(targetHeight - 20 - 1 - 1) / 2}px`
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 })
  };

  const api = useApi();

  useEffect(() => {
    if (location) {
      setValue(buildLocationOption(location));
      if( location.locationSource === "GPS" ) {
        loadDefaultOptions(location);
      }
    } else {
      setValue(null);
    }
  }, [location]);

  const loadDefaultOptions = (gpsLocation) => {
      api
        .reverseGeoLocationSearch(gpsLocation.latitude, gpsLocation.longitude)
        .then(locations => {
          var defaultOptions = [];
          locations.forEach(function(loc) {
            defaultOptions.push(buildLocationOption(loc));
          });
          setDefaultOptions(defaultOptions);
        })
        .catch(error => {
          // don't set anything for default options
        });
  }

  // When a GPS fix arrives after the user clicked "Use my location", apply it
  useEffect(() => {
    // console.log("useEffect gpsRequested: ", gpsRequested);
    // console.log("useEffect currentLocation: ", currentLocation);
    // console.log("useEffect locationStatus: ", locationStatus);
    if( !gpsRequested ) {
      return;
    }
    if( locationStatus === "denied" ) {
      setGpsRequested(false);
      return;
    }
    if( locationStatus === "granted" && currentLocation ) {
      const gpsLocation = {
        locationSource: "GPS",
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
        externalId: `gps-${currentLocation.latitude.toFixed(6)}-${currentLocation.longitude.toFixed(6)}`,
        name: `${currentLocation.latitude.toFixed(5)}, ${currentLocation.longitude.toFixed(5)} (current location)`
      };
      const option = buildLocationOption(gpsLocation);
      setValue(option);
      // loadDefaultOptions(gpsLocation);
      if (onSelect) {
        onSelect(option, gpsLocation);
      }
    }
  }, [gpsRequested, locationStatus, currentLocation]);

  const handleUseMyLocation = () => {
    setGpsRequested(true);
    // requestLocation is a no-op if already granted; triggers the prompt otherwise
    requestLocation();
  };

  const buildLocationOption = location => {
    var option = {
      value: location.externalId,
      location: location,
      name: location.name,
      address: location.address,
      streetAddress: location.streetAddress,
      unitNumber: location.unitNumber,
      locationSource: location.locationSource
    };
    if (location.locationSource === "URALITY_PROFILE") {
      option["userProfile"] = location.userProfile;
    }
    return option;
  };

  const loadOptions = (inputValue, callback) => {
    var anow = Date.now();
    lastTimestamp = anow;
    setTimeout(() => {
      var now = Date.now();
      if (lastTimestamp && now - lastTimestamp >= 1000) {
        const input = inputValue.trim().toLowerCase();
        if (input.length > 3) {
          var apiCalls = [];
          if (profileSearch) {
            apiCalls.push(profileSearchCall(name, input));
          }
          apiCalls.push(locationCall(input));
          Promise.all(apiCalls)
            .then(locations => {
              var flattened = locations.flat();
              var results = flattened.map(loc => {
                return buildLocationOption(loc);
              });
              callback(results);
            })
            .catch(error => {
              callback([]);
            });
        } else {
          callback([]);
        }
      } else {
        callback([]);
      }
    }, 1000);
  };

  const profileSearchCall = (name, input) => {
    var profilePayload = {
      input: input,
      userProfileTypes: ["BUSINESS", "POI", "COMMUNITY"]
    };

    return api.profileLocationSearch(name, profilePayload);
  };

  const locationCall = input => {
    var payload = {
      source: searchSource,
      searchCriteria: input
    };
    if(searchLocation) {
      payload.latitude = searchLocation.latitude;
      payload.longitude = searchLocation.longitude;
    }
    if( searchLocation && searchRadius ) {
      payload.searchRadius = searchRadius;
    }
    return api.geoLocationSearch(payload);
  };

  const handleChange = option => {
    if (option && option.locationSource === "GOOGLE") {
      if (option && onSelect) {
        api
          .getLocationByPlaceId(option.location.externalId)
          .then(place => {
            place["unitNumber"] = option.location.unitNumber;
            place["gpid"] = option.location.externalId;
            setValue(place);
            onSelect(option, place);
            if (clearOnSelect) {
              setValue(null);
              setInputValue("");
            }
          })
          .catch(error => {});
      } else if (returnNull && onSelect) {
        setValue(null);
        onSelect(null);
        if (clearOnSelect) {
          setInputValue("");
        }
      }
    } else {
      setValue(option);
      if (option && onSelect) {
        onSelect(option, option.location);
        if (clearOnSelect) {
          setValue(null);
          setInputValue("");
        }
      } else if (returnNull && onSelect) {
        onSelect(null);
        if (clearOnSelect) {
          setInputValue("");
        }
      }
      if( !option ) {
        setGpsRequested(false);
      }
    }
  };

  const formatOptionLabel = ({ location, userProfile }) => {
    if (location) {
      if (userProfile) {
        return (
          <div className="location-select-option-label">
            {userProfile && (
              <UralityAvatarImage
                imageId={userProfile.logoId}
                size="sm"
                name={userProfile.displayName}
                type={userProfile.userProfileType}
              />
            )}
            <div className="location-select-option-text">
              <span>
                <b>{userProfile ? userProfile.displayName : location.name}</b>
              </span>
              <span>{location.address}</span>
            </div>
          </div>
        );
      } else {
        return (
          <MapLink location={location} fullAddress={true} clickable={false} />
        );
      }
    } else {
      return <></>;
    }
  };

  const selectFromMapCallback = ft => {
    // console.log("selectFromMapCallback", ft);
    var coords = ft.geometry.coordinates;
    api
      .reverseGeoLocationSearch(coords[1], coords[0])
      .then(locations => {
        // console.log(
        //   "LocationSelector after reverse location search",
        //   locations
        // );
        var defaultOptions = locations.map((loc, index) => {
          return buildLocationOption(loc);
        });
        // locations.forEach(function(loc) {
        //   defaultOptions.push(buildLocationOption(loc));
        // });
        setDefaultOptions(defaultOptions);
        if (locations.length > 0) {
          setValue(defaultOptions[0]);
          if (onSelect) {
            onSelect(defaultOptions[0], locations[0]);
          }
        }
      })
      .catch(error => {
        // don't set anything for default options
      });
  };

  return (
    <>
    { gpsRequested ? (
      <div className="default-flex-row-layout">
        {useMyLocation && (
          <button
            type="button"
            className={`btn btn-secondary btn-sm with-icon location-selector-gps-btn${
              value?.location?.locationSource === "GPS" ? " disabled" : ""
            }`}
            disabled={value?.location?.locationSource === "GPS"}
            onClick={handleUseMyLocation}
            style={{ marginTop: "0.5rem" }}
          >
            {gpsRequested && locationStatus === "loading" ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" />
                Finding location...
              </>
            ) : (
              <>
                <i className="fa-solid fa-location-crosshairs" />
                Use my location
              </>
            )}
          </button>
        )}
        {formatOptionLabel({location: value?.location})}
        <div className="clickable with-icon" onClick={() => handleChange(null)}>
          <i className="fa-solid fa-xmark" />
        </div>
      </div>
    ) : (
      <>
      <AsyncSelect
        ref={innerRef ? innerRef : null}
        cacheOptions={!clearOnSelect}
        className={
          "location-select" +
          (isInvalid ? " is-invalid" : "") +
          (className ? " " + className : "")
        }
        styles={styles ? styles : defaultStyles}
        menuPortalTarget={document.body}
        formatOptionLabel={formatOptionLabel}
        loadOptions={loadOptions}
        defaultOptions={defaultOptions ?? []}
        isSearchable
        isClearable={isNullable}
        inputValue={inputValue}
        onInputChange={setInputValue}
        value={value}
        onFocus={() => (lastTimestamp = null)}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={placeholder ?? "Select..."}
        noOptionsMessage={() => (
          <>
            Start Typing...
            {addFromMap && (
              <button
                style={{ textAlign: "center" }}
                type="button"
                className="a with-icon"
                onClick={() =>
                  emitEvent("create_new_feature", {
                    feature: "POINT",
                    text: "Click a point on the map to set your origin.",
                    callback: selectFromMapCallback
                  })
                }
              >
                <span className="material-icons">add_location_alt</span>
                Select from map..
              </button>
            )}
          </>
        )}
      />
      <div className="invalid-feedback">Location is a required field</div>
      {useMyLocation && (
        <button
          type="button"
          className={`btn btn-secondary btn-sm with-icon location-selector-gps-btn${
            value?.location?.locationSource === "GPS" ? " disabled" : ""
          }`}
          disabled={value?.location?.locationSource === "GPS"}
          onClick={handleUseMyLocation}
          style={{ marginTop: "0.5rem" }}
        >
          {gpsRequested && locationStatus === "loading" ? (
            <>
              <i className="fa-solid fa-spinner fa-spin" />
              Finding location...
            </>
          ) : (
            <>
              <i className="fa-solid fa-location-crosshairs" />
              Use my location
            </>
          )}
        </button>
      )}
      {children}
      {moveEnabled && onMovePoint && (
        <button
          className="a with-icon"
          type="button"
          onClick={() => onMovePoint()}
        >
          <i className="fa-solid fa-up-down-left-right"></i>
          Move Pin...
        </button>
      )}
      <Collapse in={fullAddressMode && value}>
        <div>
          <div className="full-address-mode">
            <fieldset>
              <LocationAddressEntry
                location={value?.location}
                onChange={(loc, callback) => {
                  if (loc) {
                    var merged = { ...value?.location, ...loc };
                    handleChange(buildLocationOption(merged));
                  }
                  callback(true);
                }}
              />
            </fieldset>
          </div>
        </div>
      </Collapse>
      </>
    )}  
    </>
  );
};
export default LocationSelector;
